import React, { useState, useEffect, useCallback, useRef } from 'react';

const DEV_MODE_KEY = '__dev_mode';
const DEV_PASSPHRASE = 'rongviet2024';
const DEV_SHORTCUT_KEY = 'd'; // Ctrl+Shift+D x5

/**
 * Hook quan ly Dev Mode cho debug tools.
 * 
 * Cach mo khoa:
 * 1. Console: window.__devMode('rongviet2024')
 * 2. Ctrl+Shift+D nhan 5 lan lien tiep (trong 3 giay)
 * 3. Ctrl+Shift + click tieu de 5 lan (legacy)
 * 
 * Dung sessionStorage -> mat khi dong tab.
 */
export function useDevMode() {
  const [isDevMode, setIsDevMode] = useState(() => {
    return sessionStorage.getItem(DEV_MODE_KEY) === 'true';
  });

  const keyCountRef = useRef(0);
  const keyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expose window.__devMode() cho console access
  useEffect(() => {
    (window as any).__devMode = (passphrase?: string) => {
      if (passphrase === DEV_PASSPHRASE) {
        sessionStorage.setItem(DEV_MODE_KEY, 'true');
        setIsDevMode(true);
        console.log('%c[DEV MODE] Activated', 'color: #10b981; font-weight: bold; font-size: 14px');
        return 'Dev mode enabled.';
      } else if (passphrase === 'off') {
        sessionStorage.removeItem(DEV_MODE_KEY);
        setIsDevMode(false);
        console.log('%c[DEV MODE] Deactivated', 'color: #ef4444; font-weight: bold; font-size: 14px');
        return 'Dev mode disabled.';
      } else {
        console.log('%c[DEV MODE] Invalid passphrase', 'color: #f59e0b');
        return 'Usage: __devMode("passphrase") or __devMode("off")';
      }
    };

    return () => {
      delete (window as any).__devMode;
    };
  }, []);

  // Keyboard shortcut: Ctrl+Shift+D x5 trong 3 giay
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === DEV_SHORTCUT_KEY) {
        e.preventDefault();
        keyCountRef.current += 1;

        // Reset timer
        if (keyTimerRef.current) clearTimeout(keyTimerRef.current);
        keyTimerRef.current = setTimeout(() => { keyCountRef.current = 0; }, 3000);

        if (keyCountRef.current >= 5) {
          keyCountRef.current = 0;
          const newState = !sessionStorage.getItem(DEV_MODE_KEY);
          if (newState) {
            sessionStorage.setItem(DEV_MODE_KEY, 'true');
          } else {
            sessionStorage.removeItem(DEV_MODE_KEY);
          }
          setIsDevMode(!!newState);
          console.log(`%c[DEV MODE] ${newState ? 'Activated' : 'Deactivated'} via Ctrl+Shift+D x5`,
            `color: ${newState ? '#10b981' : '#ef4444'}; font-weight: bold`);
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (keyTimerRef.current) clearTimeout(keyTimerRef.current);
    };
  }, []);

  // Legacy: Secret click counter (Ctrl+Shift + click 5 lan)
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  const handleSecretClick = useCallback((e: React.MouseEvent) => {
    if (!e.ctrlKey || !e.shiftKey) return;

    const now = Date.now();
    if (now - lastClickTime > 2000) {
      setClickCount(1);
    } else {
      setClickCount(prev => prev + 1);
    }
    setLastClickTime(now);

    if (clickCount + 1 >= 5) {
      const newState = !isDevMode;
      if (newState) {
        sessionStorage.setItem(DEV_MODE_KEY, 'true');
      } else {
        sessionStorage.removeItem(DEV_MODE_KEY);
      }
      setIsDevMode(newState);
      setClickCount(0);
      console.log(`%c[DEV MODE] ${newState ? 'Activated' : 'Deactivated'} via secret click`,
        `color: ${newState ? '#10b981' : '#ef4444'}; font-weight: bold`);
    }
  }, [clickCount, lastClickTime, isDevMode]);

  return { isDevMode, handleSecretClick };
}
